import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Trophy, Users, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import apiService from '../services/api';
import { Contest, ContestLeaderboardEntry } from '../types';

const CONTEST_TYPES = [
  { value: 'order_count', label: 'Order Count' },
  { value: 'revenue',     label: 'Revenue (₹)' },
];

const REWARD_TYPES = [
  { value: 'cash',    label: 'Cash' },
  { value: 'bonus',   label: 'Bonus' },
  { value: 'gift',    label: 'Gift' },
  { value: 'voucher', label: 'Voucher' },
];

const BANNER_COLORS = [
  { value: '#880E4F', label: 'Dark Pink' },
  { value: '#1565C0', label: 'Blue' },
  { value: '#2E7D32', label: 'Green' },
  { value: '#E65100', label: 'Orange' },
  { value: '#4A148C', label: 'Purple' },
];

const emptyForm: Partial<Contest> = {
  title: '',
  description: '',
  contestType: 'order_count',
  targetValue: 0,
  rewardType: 'cash',
  rewardValue: 0,
  rewardDescription: '',
  startDate: '',
  endDate: '',
  bannerColor: '#880E4F',
  isActive: true,
};

const Label: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <label className="block text-sm font-medium text-gray-700 mb-1">{children}</label>
);

const ContestPage: React.FC = () => {
  const [contests, setContests]           = useState<Contest[]>([]);
  const [loading, setLoading]             = useState(true);
  const [showForm, setShowForm]           = useState(false);
  const [editId, setEditId]               = useState<number | null>(null);
  const [form, setForm]                   = useState<Partial<Contest>>(emptyForm);
  const [saving, setSaving]               = useState(false);
  const [leaderboardId, setLeaderboardId] = useState<number | null>(null);
  const [leaderboard, setLeaderboard]     = useState<ContestLeaderboardEntry[]>([]);
  const [lbLoading, setLbLoading]         = useState(false);

  useEffect(() => { fetchContests(); }, []);

  async function fetchContests() {
    setLoading(true);
    try {
      const res = await apiService.getContests();
      setContests(res.entity ?? []);
    } catch {
      toast.error('Failed to load contests');
    } finally {
      setLoading(false);
    }
  }

  function openCreate() {
    setForm(emptyForm);
    setEditId(null);
    setShowForm(true);
  }

  function openEdit(c: Contest) {
    setForm({
      ...c,
      startDate: c.startDate?.substring(0, 10),
      endDate:   c.endDate?.substring(0, 10),
    });
    setEditId(c.id);
    setShowForm(true);
  }

  async function handleSave() {
    if (!form.title?.trim())              return toast.error('Title is required');
    if (!form.targetValue || form.targetValue <= 0) return toast.error('Target value must be > 0');
    if (!form.startDate || !form.endDate) return toast.error('Start and end dates are required');
    if (form.endDate! < form.startDate!)  return toast.error('End date must be after start date');

    setSaving(true);
    try {
      if (editId) {
        const res = await apiService.updateContest(editId, form);
        if (!res.isSuccess) return toast.error(res.message ?? 'Update failed');
        toast.success('Contest updated');
      } else {
        const res = await apiService.createContest({ ...form, createdBy: 1 });
        if (!res.isSuccess) return toast.error(res.message ?? 'Create failed');
        toast.success('Contest created');
      }
      setShowForm(false);
      fetchContests();
    } catch {
      toast.error('Something went wrong');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: number) {
    if (!window.confirm('Deactivate this contest?')) return;
    try {
      await apiService.deleteContest(id);
      toast.success('Contest deactivated');
      fetchContests();
    } catch {
      toast.error('Failed to deactivate');
    }
  }

  async function toggleLeaderboard(id: number) {
    if (leaderboardId === id) { setLeaderboardId(null); return; }
    setLeaderboardId(id);
    setLbLoading(true);
    try {
      const res = await apiService.getContestLeaderboard(id);
      setLeaderboard(res.entity ?? []);
    } catch {
      toast.error('Failed to load leaderboard');
    } finally {
      setLbLoading(false);
    }
  }

  async function handleMarkPaid(contestId: number, technicianId: number) {
    try {
      await apiService.markContestRewardPaid(contestId, technicianId);
      toast.success('Reward marked as paid');
      // reload leaderboard
      setLeaderboardId(null);
      setTimeout(() => toggleLeaderboard(contestId), 100);
    } catch {
      toast.error('Failed to mark reward');
    }
  }

  const today = new Date().toISOString().substring(0, 10);

  function StatusBadge({ c }: { c: Contest }) {
    const start = c.startDate?.substring(0, 10) ?? '';
    const end   = c.endDate?.substring(0, 10) ?? '';
    if (!c.isActive)      return <span className="badge badge-neutral">Inactive</span>;
    if (today < start)    return <span className="badge badge-info">Upcoming</span>;
    if (today > end)      return <span className="badge badge-neutral">Ended</span>;
    return <span className="badge badge-success">Live</span>;
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Trophy className="h-7 w-7 text-pink-700" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Contests</h1>
            <p className="text-sm text-gray-500">Drive technician performance with incentive-based challenges</p>
          </div>
        </div>
        <button onClick={openCreate} className="btn-primary flex items-center gap-2">
          <Plus className="h-4 w-4" /> New Contest
        </button>
      </div>

      {/* Create / Edit form */}
      {showForm && (
        <div className="bg-white rounded-2xl shadow-md border border-gray-100 mb-8 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between"
               style={{ background: form.bannerColor ?? '#880E4F' }}>
            <h2 className="text-white font-semibold text-lg">
              {editId ? 'Edit Contest' : 'New Contest'}
            </h2>
            <button onClick={() => setShowForm(false)}
                    className="text-white/70 hover:text-white text-2xl leading-none">&times;</button>
          </div>

          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Title */}
            <div className="md:col-span-2">
              <Label>Title *</Label>
              <input className="input-field" placeholder="e.g. May Mega Drive"
                     value={form.title ?? ''}
                     onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
            </div>

            {/* Description */}
            <div className="md:col-span-2">
              <Label>Description</Label>
              <textarea className="input-field" rows={2} placeholder="Short motivating description..."
                        value={form.description ?? ''}
                        onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            </div>

            {/* Contest Type */}
            <div>
              <Label>Contest Type *</Label>
              <select className="input-field" value={form.contestType ?? 'order_count'}
                      onChange={e => setForm(f => ({ ...f, contestType: e.target.value }))}>
                {CONTEST_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>

            {/* Target */}
            <div>
              <Label>Target {form.contestType === 'revenue' ? '(₹ revenue)' : '(orders)'} *</Label>
              <input className="input-field" type="number" min={1}
                     value={form.targetValue ?? 0}
                     onChange={e => setForm(f => ({ ...f, targetValue: Number(e.target.value) }))} />
            </div>

            {/* Reward Type */}
            <div>
              <Label>Reward Type *</Label>
              <select className="input-field" value={form.rewardType ?? 'cash'}
                      onChange={e => setForm(f => ({ ...f, rewardType: e.target.value }))}>
                {REWARD_TYPES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>

            {/* Reward Value */}
            <div>
              <Label>Reward Value (₹) *</Label>
              <input className="input-field" type="number" min={0}
                     value={form.rewardValue ?? 0}
                     onChange={e => setForm(f => ({ ...f, rewardValue: Number(e.target.value) }))} />
            </div>

            {/* Reward Description */}
            <div className="md:col-span-2">
              <Label>Reward Description</Label>
              <input className="input-field" placeholder="e.g. ₹1,500 cash credited to your account"
                     value={form.rewardDescription ?? ''}
                     onChange={e => setForm(f => ({ ...f, rewardDescription: e.target.value }))} />
            </div>

            {/* Dates */}
            <div>
              <Label>Start Date *</Label>
              <input className="input-field" type="date"
                     value={form.startDate ?? ''}
                     onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))} />
            </div>
            <div>
              <Label>End Date *</Label>
              <input className="input-field" type="date"
                     value={form.endDate ?? ''}
                     onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))} />
            </div>

            {/* Banner Color */}
            <div>
              <Label>Banner Color</Label>
              <div className="flex gap-3 mt-2">
                {BANNER_COLORS.map(c => (
                  <button key={c.value} title={c.label}
                          onClick={() => setForm(f => ({ ...f, bannerColor: c.value }))}
                          style={{ background: c.value }}
                          className={`w-8 h-8 rounded-full border-2 transition-all ${
                            form.bannerColor === c.value ? 'border-gray-800 scale-125' : 'border-transparent'
                          }`} />
                ))}
              </div>
            </div>

            {/* Active toggle */}
            <div className="flex items-center gap-3 self-end pb-1">
              <Label>Active</Label>
              <input type="checkbox" className="h-5 w-5 accent-pink-700 mt-[-4px]"
                     checked={form.isActive ?? true}
                     onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} />
            </div>
          </div>

          <div className="px-6 pb-6 flex justify-end gap-3">
            <button onClick={() => setShowForm(false)} className="btn-secondary">Cancel</button>
            <button onClick={handleSave} disabled={saving} className="btn-primary">
              {saving ? 'Saving…' : editId ? 'Save Changes' : 'Create Contest'}
            </button>
          </div>
        </div>
      )}

      {/* Contest List */}
      {loading ? (
        <div className="text-center py-16 text-gray-400">Loading contests…</div>
      ) : contests.length === 0 ? (
        <div className="text-center py-20 text-gray-400">
          <Trophy className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p>No contests yet. Create one to motivate your team!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {contests.map(c => (
            <div key={c.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              {/* Color strip */}
              <div className="h-1.5 w-full" style={{ background: c.bannerColor }} />

              <div className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-gray-900 text-base">{c.title}</h3>
                      <StatusBadge c={c} />
                    </div>
                    {c.description && <p className="text-sm text-gray-500 mt-0.5">{c.description}</p>}

                    <div className="mt-3 flex flex-wrap gap-4 text-sm text-gray-600">
                      <span>
                        <strong>Goal:</strong>{' '}
                        {c.contestType === 'revenue'
                          ? `₹${c.targetValue.toLocaleString()} revenue`
                          : `${c.targetValue} orders`}
                      </span>
                      <span>
                        <strong>Reward:</strong> ₹{c.rewardValue.toLocaleString()} {c.rewardType}
                        {c.rewardDescription ? ` — ${c.rewardDescription}` : ''}
                      </span>
                      <span>
                        <strong>Period:</strong>{' '}
                        {c.startDate?.substring(0, 10)} → {c.endDate?.substring(0, 10)}
                      </span>
                    </div>
                  </div>

                  <div className="flex gap-1 shrink-0">
                    <button onClick={() => toggleLeaderboard(c.id)}
                            className="p-2 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                            title="Leaderboard">
                      <Users className="h-4 w-4" />
                    </button>
                    <button onClick={() => openEdit(c)}
                            className="p-2 rounded-lg text-gray-400 hover:text-amber-600 hover:bg-amber-50 transition-colors"
                            title="Edit">
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button onClick={() => handleDelete(c.id)}
                            className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                            title="Deactivate">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Leaderboard panel */}
                {leaderboardId === c.id && (
                  <div className="mt-4 border-t pt-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-1">
                      <Trophy className="h-4 w-4 text-amber-500" /> Leaderboard
                    </h4>
                    {lbLoading ? (
                      <p className="text-sm text-gray-400">Loading…</p>
                    ) : leaderboard.length === 0 ? (
                      <p className="text-sm text-gray-400">No participation yet.</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-gray-500 border-b text-xs uppercase tracking-wide">
                              <th className="pb-2 pr-4">Rank</th>
                              <th className="pb-2 pr-4">Technician</th>
                              <th className="pb-2 pr-4">Progress</th>
                              <th className="pb-2 pr-4">Status</th>
                              <th className="pb-2">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {leaderboard.map(entry => {
                              const pct = Math.min(100, (entry.currentValue / c.targetValue) * 100);
                              return (
                                <tr key={entry.technicianId} className="border-b last:border-0 hover:bg-gray-50">
                                  <td className="py-2.5 pr-4 font-bold text-gray-700">
                                    {entry.rank === 1 ? '🥇' : entry.rank === 2 ? '🥈' : entry.rank === 3 ? '🥉' : `#${entry.rank}`}
                                  </td>
                                  <td className="py-2.5 pr-4 font-medium">{entry.technicianName}</td>
                                  <td className="py-2.5 pr-4">
                                    <div className="flex items-center gap-2">
                                      <div className="w-24 bg-gray-100 rounded-full h-2">
                                        <div className="h-2 rounded-full transition-all"
                                             style={{ width: `${pct}%`, background: c.bannerColor }} />
                                      </div>
                                      <span className="text-gray-600 text-xs whitespace-nowrap">
                                        {c.contestType === 'revenue'
                                          ? `₹${entry.currentValue.toLocaleString()} / ₹${c.targetValue.toLocaleString()}`
                                          : `${entry.currentValue} / ${c.targetValue}`}
                                      </span>
                                    </div>
                                  </td>
                                  <td className="py-2.5 pr-4">
                                    {entry.isCompleted
                                      ? <span className="badge badge-success">Completed</span>
                                      : <span className="badge badge-info">In Progress</span>}
                                  </td>
                                  <td className="py-2.5">
                                    {entry.isCompleted && (
                                      <button onClick={() => handleMarkPaid(c.id, entry.technicianId)}
                                              className="flex items-center gap-1 text-xs text-green-700 hover:text-green-900 font-semibold">
                                        <CheckCircle className="h-3.5 w-3.5" /> Mark Paid
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContestPage;
